import { Intervention } from '../../domain/intervention';
import { Process } from '../../domain/process';
import { SamplingPoint } from '../../domain/sampling-point';
import { Zone } from '../../domain/zone';
import { mapper } from '../../tools/mapping/mapper';
import { InterventionEntity } from '../entity';
import type { AppDB } from '../db';

export class InterventionRepository {
    constructor(private readonly database: AppDB) {}


    async changeInterventionStatus(int: Intervention) {
        const ie = await this.database.getInterventionEntityByCode(int.Code)
        if (!ie || !ie.Id) {
            return;
        }
        await this.database.interventions.update(ie.Id, {
            Status: int.Status
        });
        return true;
    }

    async addIntervention(int: Intervention) {

        const ie = mapper.map(int, Intervention, InterventionEntity);

        const interventionId = await this.database.interventions.add(ie);
        if (int.SamplingPoint) {
            await this.database.attachSamplingPoint(int.SamplingPoint, interventionId);
        }

        if (int.ProcessList?.length > 0) {
            for (let i = 0; i < int.ProcessList.length; i++) {
                await this.database.attachProcess(int.ProcessList[i], interventionId);
            }
        }

        if (int.ZoneList?.length > 0) {
            for (let i = 0; i < int.ZoneList.length; i++) {
                await this.database.attachZone(int.ZoneList[i], interventionId);
            }
        }

        if (int.MesureEauList?.length > 0) {
            for (let i = 0; i < int.MesureEauList.length; i++) {
                await this.database.attachMeasureEau(int.MesureEauList[i], interventionId);
            }
        }

        if (int.MesureMassifList?.length > 0) {
            for (let i = 0; i < int.MesureMassifList.length; i++) {
                await this.database.attachMeasureMassif(int.MesureMassifList[i], interventionId);
            }
        }

        if (int.MesureLingetteList?.length > 0) {
            for (let i = 0; i < int.MesureLingetteList.length; i++) {
                await this.database.attachMeasureLingette(int.MesureLingetteList[i], interventionId);
            }
        }

        if (int.MesureAdductionAirList.length > 0) {
            for (let i = 0; i < int.MesureAdductionAirList.length; i++) {
                await this.database.attachMesureAdduction(int.MesureAdductionAirList[i], interventionId);
            }
        }

        if (int.MesureGeneriqueList.length > 0) {
            for (let i = 0; i < int.MesureGeneriqueList.length; i++) {
                await this.database.attachGeneric(int.MesureGeneriqueList[i], interventionId);
            }
        }

        if (int.Sampler) {
            await this.database.attachUser(int.Sampler, interventionId, true);
        }

        if (int.Contact) {
            await this.database.attachUser(int.Contact, interventionId, false);
        }

        if (int.SituationPlan) {
            await this.database.attachAreaMapToIntervention(int.SituationPlan, interventionId)
        }
        // for (let i = 0; i < int.ZPList.length; i++) {
        //     if (int.ZPList[i] instanceof Zone) {
        //         await this.database.attachZone(int.ZPList[i] as Zone, interventionId);
        //     } else if (int.ZPList[i] instanceof Process) {
        //         await this.database.attachProcess(int.ZPList[i] as Process, interventionId);
        //     }
        // }
        if (int.LingetteAreaMapTerrainList?.length > 0) {
            for (let i = 0; i < int.LingetteAreaMapTerrainList.length; i++) {
                await this.database.attachLingetteAreaMapTerrain(int.LingetteAreaMapTerrainList[i], interventionId);
            }
        }

        return interventionId;
    }

    async getInterventionEntityById(id: number) {
        return await this.database.interventions.where('Id').equals(id).first(async ie => {
            return ie;
        });
    }

    async getInterventionEntityByCode(code: number) {
        return await this.database.interventions.where('Code').equals(code).first(async ie => {
            return ie;
        });
    }

    async getInterventionList() {
        const interventionList = await Promise.all(
            await this.database.interventions.toArray(ies => {
                return ies.map(async ie => {
                    // const intervention = mapper.map(ie, InterventionEntity, Intervention);
                    // if (ie?.SamplingPointId) {
                    //     intervention.SamplingPoint = await this.database.getSamplingPointById(ie.SamplingPointId);
                    // }
                    const intervention = await this.database.getInterventionByCode(ie.Code);
                    return intervention;
                });
            })
        );
        return interventionList;
    }

    async getInterventionByCode(code: number) {
        return await this.database.interventions.where('Code').equals(code).first(async ie => {
            const intervention = mapper.map(ie, InterventionEntity, Intervention);

            if (ie?.SamplingPointId) {
                intervention.SamplingPoint = await this.database.getSamplingPointById(ie.SamplingPointId);
            }

            if (ie?.ProcessIds) {
                for (let i = 0; i < ie.ProcessIds.length; i++) {
                    intervention.ProcessList.push(await this.database.getProcessByIdWithChildren(ie.ProcessIds[i]));
                }
            }

            if (ie?.ZoneIds) {
                for (let i = 0; i < ie.ZoneIds.length; i++) {
                    intervention.ZoneList.push(await this.database.getZoneByIdWithChildren(ie.ZoneIds[i]));
                }
            }

            if (ie?.LingetteIds) {
                for (let i = 0; i < ie.LingetteIds.length; i++) {
                    intervention.MesureLingetteList.push(await this.database.getMeasureLingetteById(ie.LingetteIds[i]));
                }
            }

            if (ie?.EauIds) {
                for (let i = 0; i < ie.EauIds.length; i++) {
                    intervention.MesureEauList.push(await this.database.getMeasureEauById(ie.EauIds[i]));
                }
            }

            if (ie?.MassifIds) {
                for (let i = 0; i < ie.MassifIds.length; i++) {
                    intervention.MesureMassifList.push(await this.database.getMeasureMassifById(ie.MassifIds[i]));
                }
            }

            if (ie?.AdductionIds) {
                for (let i = 0; i < ie.AdductionIds.length; i++) {
                    intervention.MesureAdductionAirList.push(await this.database.getMesureAdductionByIdWithChildren(ie.AdductionIds[i]));
                }
            }

            if (ie?.GenericIds) {
                for (let i = 0; i < ie.GenericIds.length; i++) {
                    intervention.MesureGeneriqueList.push(await this.database.getMesureGenericById(ie.GenericIds[i]));
                }
            }

            if (ie?.ContactId) {
                intervention.Contact = await this.database.getUserById(ie.ContactId);
            }

            if (ie?.AreaMapId) {
                intervention.SituationPlan = await this.database.getAreaMapById(ie.AreaMapId)
            }

            if (ie?.LingetteAreaMapTerrainIds) {
                for (let i = 0; i < ie.LingetteAreaMapTerrainIds.length; i++) {
                    intervention.LingetteAreaMapTerrainList.push(await this.database.getAreaMapById(ie.LingetteAreaMapTerrainIds[i]));
                }
            }

            return intervention;
        })
    }

    async getInterventionWithLingettes(code: number) {
        return await this.database.interventions.where('Code').equals(code).first(async ie => {
            const intervention = mapper.map(ie, InterventionEntity, Intervention);

            if (ie?.LingetteIds) {
                for (let i = 0; i < ie.LingetteIds.length; i++) {
                    intervention.MesureLingetteList.push(await this.database.getMeasureLingetteById(ie.LingetteIds[i]));
                }
            }

            return intervention;
        })
    }

    async deleteIntervention(code: number) {
        await this.database.interventions.where('Code').equals(code).first(async ie => {

            if (ie?.GenericIds && ie?.GenericIds?.length > 0) {
                for (let i = 0; i < ie.GenericIds.length; i++) {
                    await this.database.measureGeneric.where('Id').equals(ie.GenericIds[i]).delete();
                }
            }
            if (ie?.SamplingPointId) {
                await this.database.samplingPoints.where('Id').equals(ie?.SamplingPointId).delete();
            }

            if (ie?.ProcessIds && ie?.ProcessIds?.length > 0) {
                for (let i = 0; i < ie.ProcessIds.length; i++) {

                    await this.database.process.where('Id').equals(ie.ProcessIds[i]).toArray(async pes => {
                        if (!pes)
                            return;
                        for (let j = 0; j < pes.length; j++) {

                            const pe = await this.database.getProcessMeasureEntityById(pes[j].MeasureId!);

                            for (let m = 0; m < pe?.OperatorIds?.length!; m++) {
                                const oe = await this.database.getOperatorEntityById(pe!.OperatorIds![m])
                                for (let n = 0; n < oe?.CassetteIds?.length!; n++) {

                                    const ce = await this.database.getCassetteEntityById(oe!.CassetteIds![n])
                                    for (let l = 0; ce?.PlageHoraireIds && l < ce.PlageHoraireIds.length; l++) {
                                        const phe = await this.database.getPlageHoraireEntityById(ce?.PlageHoraireIds[l]!)
                                        await this.database.plageHoraires.where('Id').equals(phe?.Id!).delete();
                                    }
                                    await this.database.cassettes.where('Id').equals(ce?.Id!).delete();
                                    await this.database.pompes.where('Id').equals(ce?.PompeId!).delete();
                                }
                                if (oe?.TaskIds) {
                                    this.database.clearTaskyId(oe?.TaskIds!);
                                }
                                await this.database.operators.where('Id').equals(oe?.Id!).delete();
                            }
                            await this.database.securites.where('Id').equals(pe?.SecuriteId!).delete();
                            await this.database.processSamplingConditions.where('Id').equals(pe!.ProcessSamplingConditionId!).delete();
                            await this.database.processCalculations.where('Id').equals(pes[j].ProcessCalculationId!).delete();
                            await this.database.clearProcessTechniqueById(pes[j].ProcessTechniqueIds!);
                            await this.database.clearMeanOfPreventById(pes[j].MeanOfPreventionIds!);
                            await this.database.clearGlobalTaskById(pes[j].GlobalTaskIds!);
                            if (pes[j].AreaMapId) {
                                await this.database.areaMaps.where('Id').equals(pes[j].AreaMapId!).delete();
                            }
                            await this.database.processMeasures.where('Id').equals(pe?.Id!).delete();
                        }
                    });
                    await this.database.process.where('Id').equals(ie.ProcessIds[i]).delete();
                }
            }

            if (ie?.ZoneIds && ie?.ZoneIds?.length > 0) {
                for (let i = 0; i < ie.ZoneIds.length; i++) {

                    await this.database.zones.where('Id').equals(ie.ZoneIds[i]).toArray(async zes => {
                        if (!zes)
                            return;
                        for (let j = 0; j < zes.length; j++) {
                            for (let k = 0; k < zes[j].MeasureIds?.length!; k++) {
                                const zme = await this.database.getZoneMeasureEntityById(zes[j].MeasureIds![k]);

                                const ce = await this.database.getCassetteEntityById(zme!.CassetteId!)
                                for (let l = 0; ce?.PlageHoraireIds && l < ce.PlageHoraireIds.length; l++) {
                                    const phe = await this.database.getPlageHoraireEntityById(ce?.PlageHoraireIds[l]!)
                                    await this.database.plageHoraires.where('Id').equals(phe?.Id!).delete();
                                }
                                await this.database.cassettes.where('Id').equals(ce?.Id!).delete();
                                await this.database.pompes.where('Id').equals(ce?.PompeId!).delete();

                                await this.database.zoneSamplingConditions.where('Id').equals(zme!.SamplingConditionId!).delete();
                                await this.database.securites.where('Id').equals(zme?.PoseSecuriteId!).delete();
                                if (zme?.DePoseSecuriteId) {
                                    await this.database.securites.where('Id').equals(zme?.DePoseSecuriteId).delete();
                                }
                                if (zes[j] && zes[j].MPCAIds !== undefined) {
                                    await this.database.clearMPCAById(zes[j].MPCAIds!);
                                }
                                if (zes[j].AreaMapIds) {
                                    await this.database.clearAreaMapById(zes[j]?.AreaMapIds!);
                                }
                                await this.database.mesures.where('Id').equals(zme?.Id!).delete();
                            }
                        }
                    })
                    await this.database.zones.where('Id').equals(ie.ZoneIds[i]).delete();
                }
            }

            if (ie?.EauIds && ie?.EauIds?.length > 0) {
                for (let i = 0; i < ie.EauIds.length; i++) {
                    await this.database.measureEau.where('Id').equals(ie.EauIds[i]).delete();
                }
            }

            if (ie?.MassifIds && ie?.MassifIds?.length > 0) {
                for (let i = 0; i < ie.MassifIds.length; i++) {
                    await this.database.measureMassif.where('Id').equals(ie.MassifIds[i]).delete();
                }
            }

            if (ie?.LingetteIds && ie?.LingetteIds?.length > 0) {
                for (let i = 0; i < ie.LingetteIds.length; i++) {
                    await this.database.measureLingette.where('Id').equals(ie.LingetteIds[i]).delete();
                }
            }

            if (ie?.SamplerId) {
                await this.database.users.where('Id').equals(ie?.SamplerId).delete();
            }

            if (ie?.ContactId) {
                await this.database.users.where('Id').equals(ie?.ContactId).delete();
            }

            if (ie?.AreaMapId) {
                await this.database.areaMaps.where('Id').equals(ie?.AreaMapId).delete();
            }

            if (ie?.LingetteAreaMapTerrainIds && ie?.LingetteAreaMapTerrainIds?.length > 0) {
                for (let i = 0; i < ie.LingetteAreaMapTerrainIds.length; i++) {
                    await this.database.clearAreaMapById([ie.LingetteAreaMapTerrainIds[i]]);
                }
            }

            await this.database.interventions.where('Code').equals(code).delete();
        })
    }
}
